import type { LucideIcon } from 'lucide-react'

import {
  cn,
  Field,
  FOCUS_RING,
  Tooltip,
  useDisabled,
  useEditorialSurface,
} from '@/components/base'

/**
 * The two visual languages the control is worn in.
 *
 * `internal` is the inset track with a raised active pill, built from the
 * palette. `editorial` is the outlined row of the white Customer and Public
 * surface, where the chosen option is filled black.
 *
 * Which one applies is read from the surface rather than restated at every
 * call site — the same way `ModalFooter` picks its button. `tone` is only for
 * a caller that has to override that.
 */
const SKINS = {
  internal: {
    row: 'self-start rounded-lg bg-border/70 p-0.5 gap-0',
    option: 'h-7 rounded-md text-xs',
    box: (icon: boolean) => (icon ? 'w-7' : 'px-3'),
    active: 'bg-card font-medium text-foreground shadow-sm',
    idle: 'text-muted-foreground hover:text-foreground',
  },
  editorial: {
    row: 'w-full gap-1.5',
    option:
      'h-10 flex-1 rounded-lg border border-neutral-200 text-sm font-medium',
    box: (icon: boolean) => (icon ? 'w-10 flex-none' : 'px-3'),
    active: 'border-neutral-900 bg-neutral-900 text-white',
    idle: 'text-neutral-700 hover:border-neutral-400',
  },
} as const

type SegmentedControlProps<T extends string | number> = {
  /**
   * A written option, or a glyph with the label read out on hover — the second
   * for a choice whose options are pictures of themselves, like a grid or a
   * list view. `tooltip` is for a written option too short to say what it
   * means on its own.
   */
  options: {
    value: T
    label: string
    icon?: LucideIcon
    tooltip?: string
  }[]
  value: T
  onChange: (value: T) => void
  /** Above the row, as every other control carries its label. */
  label?: string
  /** One line underneath explaining the choice. */
  helper?: string
  disabled?: boolean
  tone?: keyof typeof SKINS
  className?: string
}

/** A short, mutually exclusive choice, shown in full rather than in a list. */
export function SegmentedControl<T extends string | number>({
  options,
  value,
  onChange,
  label,
  helper,
  disabled = false,
  tone,
  className,
}: SegmentedControlProps<T>) {
  const editorial = useEditorialSurface()
  const skin = SKINS[tone ?? (editorial ? 'editorial' : 'internal')]
  const refused = useDisabled(disabled)

  const row = (
    <div role="radiogroup" className={cn('flex flex-row', skin.row, className)}>
      {options.map(option => {
        const isActive = value === option.value
        const Icon = option.icon
        const button = (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={Icon ? option.label : undefined}
            disabled={refused}
            onClick={() => onChange(option.value)}
            className={cn(
              'flex items-center justify-center transition-colors',
              skin.option,
              skin.box(Boolean(Icon)),
              FOCUS_RING,
              isActive ? skin.active : skin.idle,
              refused && !isActive && 'opacity-50',
            )}
          >
            {Icon ? <Icon size={15} aria-hidden /> : option.label}
          </button>
        )

        const hint = option.tooltip ?? (Icon ? option.label : undefined)
        return hint ? (
          <Tooltip key={option.value} label={hint}>
            {button}
          </Tooltip>
        ) : (
          button
        )
      })}
    </div>
  )

  if (!label && !helper) return row

  // The row is a `radiogroup`, not a focusable control, so there is no id for
  // a `<label htmlFor>` to point at — `Field`'s render prop is ignored here on
  // purpose and the group carries its own name.
  return (
    <Field label={label} helper={helper}>
      {() => row}
    </Field>
  )
}
